import React, { useEffect, useState } from "react";
import { Smartphone, Bell, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, subscribeToPush } from "../lib/push";

/**
 * Sticky "install app" hero banner that combines PWA install + push subscribe.
 * Always shows the sticker (unless dismissed). If the browser fires
 * `beforeinstallprompt`, the sticker becomes a real install button.
 */
export const PwaPromoBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("guzz_pwa_promo_dismissed") === "1") {
      setDismissed(true);
    }
    // Detect if the app is already running as installed PWA
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
    }
    const bip = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", bip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", bip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      setBusy(true);
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          setDeferred(null);
          toast.success("App installata. Ora attiva le notifiche.");
          if (isPushSupported()) {
            try {
              await subscribeToPush();
              toast.success("Notifiche attivate");
            } catch { /* silent */ }
          }
        }
      } finally { setBusy(false); }
    } else {
      // iOS Safari fallback - show instructions
      toast.info("Su iPhone: tocca l'icona di condivisione in Safari, poi 'Aggiungi a Home'.", { duration: 6000 });
    }
  };

  const dismiss = () => {
    localStorage.setItem("guzz_pwa_promo_dismissed", "1");
    setDismissed(true);
  };

  if (dismissed || installed) return null;

  return (
    <div
      className="relative overflow-hidden border-y border-gold/30"
      data-testid="pwa-promo-banner"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background: "radial-gradient(600px 200px at 20% 30%, rgba(212,175,55,0.35), transparent 55%), radial-gradient(400px 180px at 80% 70%, rgba(212,175,55,0.3), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-6 md:py-8 flex items-center gap-4 md:gap-8 flex-wrap md:flex-nowrap">
        <div className="hidden md:flex shrink-0 w-14 h-14 items-center justify-center bg-gold/15 border border-gold/40 rounded-lg">
          <Smartphone size={26} className="text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-1.5">
            <Sparkles size={11} /> Aggiungi al telefono
          </div>
          <h3 className="font-display text-xl md:text-2xl text-white leading-tight">
            Ricevi <span className="text-gold italic">al volo</span> le nuove auto e le promozioni
          </h3>
          <p className="text-xs md:text-sm text-white/60 mt-1 leading-relaxed">
            Installa Guzzardella Auto come app e ti avviso subito quando arrivano occasioni.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
          <button
            onClick={install}
            disabled={busy}
            className="btn-gold flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
            data-testid="pwa-install-btn"
          >
            <Bell size={13} />
            {busy ? "..." : deferred ? "Installa ora" : "Come si fa"}
          </button>
          <button
            onClick={dismiss}
            className="p-2 text-white/40 hover:text-white transition-colors"
            aria-label="Chiudi"
            data-testid="pwa-promo-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaPromoBanner;
